import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';
import {
  ControlErrorDirective,
  HowdenTableComponent,
  IButtonActionData,
  IHowdenYesNoData,
  IPageOf,
  IResponse,
  ITableEventData,
  PageTitleComponent,
  YesNoService
} from '@howdeniberia/core-front';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ISearchResult } from 'src/app/core/models/api/sane-project/arpa-model';
import { SaneProjectContract } from 'src/app/core/services/api/projects';
import { ElectronicSignatureContract } from 'src/app/core/services/api/signature';
import { SearchMapper } from './mappers/search.mapper';
import { SearchResultViewModel, SearchViewModel } from './models';

@Component({
  selector: 'howden-sane-project-search',
  templateUrl: './sane-project-search.component.html',
  styleUrls: ['./sane-project-search.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatButtonModule,
    ControlErrorDirective,
    PageTitleComponent,
    HowdenTableComponent
  ]
})
export class SaneProjectSearchComponent implements OnInit {
  private router = inject(Router);
  private saneProjectSrv = inject(SaneProjectContract);
  private electronicSignatureSrv = inject(ElectronicSignatureContract);
  private confirmDialogSrv = inject(YesNoService);

  model = new SearchViewModel();
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.refresh();
    this.onChanges();
  }

  onServerSideConfigChanged(event: ITableEventData): void {
    this.model.updateServerSideConfig(event);
    this.refresh();
  }

  onRowClicked(event: SearchResultViewModel): void {
    this.router.navigate(['sane-projects/edit', event.projectId]);
  }

  onAction(event: IButtonActionData): void {
    if (event.buttonName === 'download-report') {
      this.downloadReport(event);
    } else if (event.buttonName === 'sign-project') {
      this.signProject(event);
    }
  }

  private refresh(): void {
    const req = { ...this.model.request } as any;
    if (!req.projectDateFrom) { req.projectDateFrom = null; }
    if (!req.projectDateTo) { req.projectDateTo = null; }

    this.saneProjectSrv
      .search(req)
      .subscribe({
        next: (searchResult: IPageOf<ISearchResult>) => {
          this.model.length = searchResult.totalCount;
          this.model.data = searchResult.data.map(src =>
            SearchMapper.fromDtoToSResultVM(src)
          );
        },
        error: err => {
          console.error('Error loading projects', err);
        }
      });
  }

  private onChanges(): void {
    this.model.filterForm.valueChanges
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.model.request.pageNumber = 0;
        this.model.updateServerSideFilters();
        this.refresh();
      });
  }

  private downloadReport(event: IButtonActionData): void {
    const id = event.row.projectId!;
    this.saneProjectSrv
      .generateReport(id)
      .subscribe({
        next: (blob: Blob) => {
          const file = new Blob([blob], { type: 'application/pdf' });
          const fileURL = URL.createObjectURL(file);

          window.open(fileURL, '_blank');

          const a = document.createElement('a');
          a.href = fileURL;
          a.download = `Project_${id}_Report.pdf`;
          a.click();

          setTimeout(() => URL.revokeObjectURL(fileURL), 10_000);
        },
        error: err => {
          console.error('Error generating report', err);
        }
      });
  }

  private signProject(event: IButtonActionData): void {
    const projectId = event.row.projectId!;

    this.saneProjectSrv
      .canBeSigned(projectId)
      .subscribe((response: IResponse<boolean>) => {
        if (response && response.result === true) {
          const url = this.electronicSignatureSrv.getElectronicSignatureUrl(projectId);
          window.open(url, '_blank');
        } else {
          const title = $localize`:@@app.sane-projects.search.sign.project.dialog.title:Información`;
          const subtitle = $localize`:@@app.sane-projects.search.sign.project.dialog.subtitle:No es posible firmar el proyecto al faltar datos necesarios para la firma, por ejemplo, el NIF o la dirección de correo electrónico del cliente.`;
          const options = {
            icon: 'help',
            title: title,
            subTitle: subtitle,
            allowCancel: false,
            okActionText: $localize`:@@app.core.actions.ok:Aceptar`,
            question: '',
            moreText: '',
            disableClose: true
          } as IHowdenYesNoData;

          this.confirmDialogSrv.open(options).subscribe();
        }
      });
  }
}
